import {
  Show,
  TextField,
  ReferenceField,
  DateField,
  NumberField,
  FunctionField,
  Labeled,
  TabbedShowLayout,
} from 'react-admin';
import { Box } from '@mui/material';
import { Printing, Machine } from '../../types';
import { calculatePrintingProgress, NUMBER_FORMATS, DATETIME_FORMAT } from '../../utils';
import { ChipList } from '../common';
import { PrintingTagsList } from './PrintingTagsList';
import { PrintingShowActions } from './PrintingShowActions';
import { PrintingReportButton } from './PrintingReportButton';
import { ZOffsetField, SoftwareVersionField, SlicerVersionField } from './fields';
import { PrintingFillamentTags } from './PrintingFillamentTags';
import { PrintingMaterialSelectedWebcad } from './PrintingMaterialSelectedWebcad';
import { PrintingInfo } from './PrintingInfo';

export const PrintingShow = () => {
  return (
    <Show actions={<PrintingShowActions />}>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Summary">
          <Box
            sx={{
              p: 2,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            <Labeled label="ID">
              <TextField source="id" />
            </Labeled>
            <Labeled label="UUID">
              <TextField source="uuid" />
            </Labeled>
            <Labeled label="Order ID">
              <TextField source="orderId" />
            </Labeled>
            <Labeled label="Foot">
              <TextField source="foot" />
            </Labeled>

            <Labeled label="Machine Serial">
              <ReferenceField source="machineId" reference="machines" link={false}>
                <FunctionField
                  render={(machine: Machine) =>
                    machine ? (
                      <ChipList
                        items={[
                          {
                            id: machine.id,
                            label: machine.serial,
                            to: `/machines/${machine.id}/show`,
                          },
                        ]}
                      />
                    ) : null
                  }
                />
              </ReferenceField>
            </Labeled>

            <Labeled label="Start Time">
              <DateField source="startTime" showTime options={DATETIME_FORMAT} />
            </Labeled>
            <Labeled label="End Time">
              <DateField source="endTime" showTime options={DATETIME_FORMAT} />
            </Labeled>
            <Labeled label="Meters">
              <NumberField source="meters" options={NUMBER_FORMATS.meters} />
            </Labeled>

            <Labeled label="Current Line">
              <NumberField source="currentLine" />
            </Labeled>
            <Labeled label="Total Lines">
              <NumberField source="totalLines" />
            </Labeled>
            <Labeled label="Progress">
              <FunctionField render={(record: Printing) => calculatePrintingProgress(record)} />
            </Labeled>
            <Labeled label="Weight">
              <NumberField source="weight" options={NUMBER_FORMATS.weight} />
            </Labeled>

            <Labeled label="Expected Weight">
              <NumberField source="expectedWeight" options={NUMBER_FORMATS.weight} />
            </Labeled>
            <Labeled>
              <SoftwareVersionField />
            </Labeled>
            <Labeled>
              <SlicerVersionField />
            </Labeled>

            <Labeled>
              <ZOffsetField />
            </Labeled>
            <Labeled>
              <PrintingMaterialSelectedWebcad />
            </Labeled>
            <Labeled>
              <PrintingFillamentTags />
            </Labeled>

            <Labeled label="System User">
              <ReferenceField source="systemUserId" reference="system-users" link={false}>
                <TextField source="username" />
              </ReferenceField>
            </Labeled>
            <Labeled label="Failure Cause">
              <ReferenceField source="failureCauseId" reference="failure-causes" link="show">
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
          </Box>
          <PrintingReportButton />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Visualizations">
          <PrintingInfo />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Associated Tags">
          <PrintingTagsList />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  );
};
